/** Typed client for the agency CRM over /v1/crm (the same module customers use; the server scopes it to the agency). */
export type CrmPath = `/v1/${string}`;
export type CrmQuery = Record<string, string | number | boolean | null | undefined>;
/** Structural subset of the surface `Http` transports, so any surface can pass its own. */
export interface CrmHttp {
  get<T>(path: CrmPath, query?: CrmQuery): Promise<T>;
  post<T>(path: CrmPath, body?: unknown, opts?: { idempotencyKey?: string }): Promise<T>;
  patch<T>(path: CrmPath, body?: unknown): Promise<T>;
}

export type CrmRow = Record<string, unknown> & { id: string };
export type CrmEntityPath = 'contacts' | 'companies' | 'deals' | 'tasks' | 'activities';
export interface Availability { owner: 'partner' | 'tenant'; canWrite: boolean; isAdmin: boolean }
export interface ImportPreview { id: string; totalRows: number; creates: number; updates: number; duplicates: number; rejected: number }

const IMPORT_COLUMNS: Record<string, string> = { name: 'name', email: 'email', phone: 'phone', title: 'title' };

/** Header row -> API mapping for the known columns (case-insensitive). */
export function mappingFromCsv(csv: string): Record<string, string> {
  const header = csv.split(/\r?\n/, 1)[0] ?? '';
  const out: Record<string, string> = {};
  for (const raw of header.split(',')) {
    const col = raw.trim().replace(/^"|"$/g, '');
    const target = IMPORT_COLUMNS[col.toLowerCase()];
    if (target) out[col] = target;
  }
  return out;
}

export function agencyCrmApi(http: CrmHttp) {
  return {
    /** 404 feature_disabled while crm.partner_crm is off. */
    availability: () => http.get<Availability>('/v1/crm/availability'),
    list: (entity: CrmEntityPath | 'leads', query: CrmQuery = {}) => http.get<CrmRow[]>(`/v1/crm/${entity}`, { limit: 200, ...query }),
    create: (entity: CrmEntityPath | 'leads', body: Record<string, unknown>) => http.post<{ id: string }>(`/v1/crm/${entity}`, body),
    update: (entity: CrmEntityPath, id: string, body: Record<string, unknown>) => http.patch<CrmRow>(`/v1/crm/${entity}/${encodeURIComponent(id)}`, body),
    markContacted: (id: string) => http.post<unknown>(`/v1/crm/leads/${encodeURIComponent(id)}/contacted`, {}),
    importPreview: (csv: string, mapping: Record<string, string>, key: string) => http.post<ImportPreview>('/v1/crm/imports', { csv, mapping }, { idempotencyKey: key }),
    importCommit: (id: string) => http.post<unknown>(`/v1/crm/imports/${encodeURIComponent(id)}/commit`, {}),
  };
}
export type AgencyCrmApi = ReturnType<typeof agencyCrmApi>;
